import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  WbSunny as SunnyIcon,
  Cloud as CloudIcon,
  CloudQueue as PartlyCloudyIcon,
  Thunderstorm as ThunderstormIcon,
  AcUnit as SnowIcon,
  WaterDrop as RainIcon,
  Visibility as FogIcon,
  Thermostat as ThermostatIcon,
  Air as WindIcon,
  Opacity as HumidityIcon
} from '@mui/icons-material';
import { fetchWeatherForDates, getTripWeatherDates } from '../utils/tripWeatherUtils';

const WeatherWidget = ({ location, tripDates, variant = 'default', headerSize = 'default' }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isHeader = variant === 'header';
  const isLargeHeader = isHeader && headerSize === 'large';

  const getWeatherIcon = (condition) => {
    const conditionLower = condition?.toLowerCase() || '';
    const iconSize = isHeader ? (isLargeHeader ? '1.85rem' : '1.25rem') : '2rem';

    if (conditionLower.includes('sun') || conditionLower.includes('clear')) {
      return <SunnyIcon sx={{ color: '#FFA726', fontSize: iconSize }} />;
    }
    if (conditionLower.includes('cloud') && !conditionLower.includes('partly')) {
      return <CloudIcon sx={{ color: isHeader ? 'rgba(255,255,255,0.85)' : '#90A4AE', fontSize: iconSize }} />;
    }
    if (conditionLower.includes('partly') || conditionLower.includes('few clouds')) {
      return <PartlyCloudyIcon sx={{ color: isHeader ? 'rgba(255,255,255,0.75)' : '#B0BEC5', fontSize: iconSize }} />;
    }
    if (conditionLower.includes('thunder') || conditionLower.includes('storm')) {
      return <ThunderstormIcon sx={{ color: '#5C6BC0', fontSize: iconSize }} />;
    }
    if (conditionLower.includes('snow') || conditionLower.includes('blizzard')) {
      return <SnowIcon sx={{ color: '#E1F5FE', fontSize: iconSize }} />;
    }
    if (conditionLower.includes('rain') || conditionLower.includes('drizzle') || conditionLower.includes('shower')) {
      return <RainIcon sx={{ color: '#42A5F5', fontSize: iconSize }} />;
    }
    if (conditionLower.includes('fog') || conditionLower.includes('mist') || conditionLower.includes('haze')) {
      return <FogIcon sx={{ color: isHeader ? 'rgba(255,255,255,0.7)' : '#BDBDBD', fontSize: iconSize }} />;
    }

    return <CloudIcon sx={{ color: isHeader ? 'rgba(255,255,255,0.85)' : '#90A4AE', fontSize: iconSize }} />;
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const tripStart = tripDates?.start;
  const tripEnd = tripDates?.end;

  useEffect(() => {
    if (!location) {
      return undefined;
    }

    const fetchWeather = async () => {
      setLoading(true);
      setError(null);

      try {
        const API_KEY = 'fc33e3038522468e9a7225228251110';

        if (!API_KEY) {
          throw new Error('Weather API key not configured');
        }

        const dateKeys = getTripWeatherDates({ start: tripStart, end: tripEnd });
        const processedData = await fetchWeatherForDates(location, dateKeys, API_KEY);

        if (!processedData.length) {
          throw new Error('No forecast data available');
        }

        setWeatherData(processedData);
      } catch (err) {
        console.error('Weather fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
    return undefined;
  }, [location, tripStart, tripEnd]);

  const wrapperSx = isHeader
    ? {
        display: 'inline-flex',
        justifyContent: 'center',
        flexShrink: 0,
      }
    : { mb: 2 };

  if (loading) {
    if (isHeader) {
      return (
        <Box sx={wrapperSx}>
          <CircularProgress size={isLargeHeader ? 24 : 18} sx={{ color: 'rgba(255,255,255,0.85)' }} />
        </Box>
      );
    }

    return (
      <Card sx={wrapperSx}>
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Loading weather data...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    if (isHeader) {
      return null;
    }

    return (
      <Card sx={wrapperSx}>
        <CardContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Weather data unavailable
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!weatherData || weatherData.length === 0) {
    return null;
  }

  const headerContent = (
    <Box sx={{
      display: 'inline-flex',
      alignItems: 'stretch',
      gap: isLargeHeader ? { xs: 0.75, sm: 1 } : { xs: 0.5, sm: 0.75 },
      px: isLargeHeader ? { xs: 1.25, sm: 1.75 } : { xs: 0.75, sm: 1 },
      py: isLargeHeader ? 0.85 : 0.5,
      borderRadius: 999,
      bgcolor: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.18)',
      backdropFilter: 'blur(8px)',
    }}>
      {weatherData.map((day, index) => (
        <Box
          key={day.date}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: isLargeHeader ? { xs: 58, sm: 72 } : { xs: 46, sm: 52 },
            px: isLargeHeader ? { xs: 0.75, sm: 1 } : { xs: 0.5, sm: 0.75 },
            ...(index > 0 && {
              borderLeft: '1px solid rgba(255,255,255,0.16)',
              pl: isLargeHeader ? { xs: 1.25, sm: 1.5 } : { xs: 1, sm: 1.25 },
            }),
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.78)',
              fontSize: isLargeHeader ? '0.68rem' : '0.58rem',
              fontWeight: 600,
              lineHeight: 1.1,
              mb: isLargeHeader ? 0.25 : 0.15,
              textTransform: 'uppercase',
              letterSpacing: 0.15,
            }}
          >
            {formatDate(day.date)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: isLargeHeader ? 0.25 : 0.15 }}>
            {getWeatherIcon(day.condition)}
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: isLargeHeader ? { xs: '0.95rem', sm: '1.1rem' } : { xs: '0.78rem', sm: '0.82rem' },
              lineHeight: 1,
            }}
          >
            {day.temp.max}°
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const content = isHeader ? headerContent : (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ThermostatIcon sx={{ mr: 1, color: 'primary.main', fontSize: 24 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          3-Day Weather Forecast
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {weatherData.map((day, index) => (
          <Box
            key={day.date}
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: index === 0 ? 'primary.50' : 'background.paper',
              transition: 'all 0.2s ease',
              minWidth: 0,
              '&:hover': {
                backgroundColor: 'primary.50',
                transform: 'translateX(4px)',
                boxShadow: 2,
              },
            }}
          >
            <Box sx={{
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 60,
              flexShrink: 0,
            }}>
              {day.icon ? (
                <img
                  src={`https:${day.icon}`}
                  alt={day.condition}
                  style={{ width: '2.5rem', height: '2.5rem' }}
                />
              ) : (
                getWeatherIcon(day.condition)
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.25, color: 'text.secondary' }}>
                {formatDate(day.date)}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.25, color: 'text.primary' }}>
                {day.temp.max}° / {day.temp.min}°
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.875rem',
                  backgroundColor: 'primary.100',
                  color: 'primary.700',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  display: 'inline-block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {day.condition}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HumidityIcon sx={{ fontSize: '0.95rem', color: 'info.main' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {day.humidity}%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WindIcon sx={{ fontSize: '0.95rem', color: 'info.main' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {day.windSpeed}m/s
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
        Weather data provided by WeatherAPI.com
      </Typography>
    </>
  );

  if (isHeader) {
    return <Box sx={wrapperSx}>{content}</Box>;
  }

  return (
    <Card sx={wrapperSx}>
      <CardContent>{content}</CardContent>
    </Card>
  );
};

export default WeatherWidget;
